
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { PrizeAllocation } from "@/components/admin/prize-allocations/types";

export function usePrizeAllocationForm(onSuccess: () => void) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    university_id: "",
    prize_id: "",
    daily_limit: 1,
  });

  const checkExistingAllocation = async (universityId: number, prizeId: number, currentAllocationId?: number) => {
    console.log('Checking existing allocation:', { universityId, prizeId, currentAllocationId });
    const { data, error } = await supabase
      .from('prize_allocations')
      .select('id')
      .eq('university_id', universityId)
      .eq('prize_id', prizeId)
      .maybeSingle();

    console.log('Check result:', { data, error });

    if (error) {
      console.error('Error checking allocation:', error);
      return false;
    }

    if (!data) {
      return false;
    }

    if (currentAllocationId && data.id === currentAllocationId) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent, editingAllocation: PrizeAllocation | null) => {
    e.preventDefault();
    
    try {
      console.log('Submitting form data:', formData);

      const universityId = parseInt(formData.university_id);
      const prizeId = parseInt(formData.prize_id);

      const exists = await checkExistingAllocation(
        universityId,
        prizeId,
        editingAllocation?.id
      );

      if (exists) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "An allocation for this university and prize combination already exists",
        });
        return;
      }
      
      if (editingAllocation) {
        const { error } = await supabase
          .from('prize_allocations')
          .update({
            university_id: universityId,
            prize_id: prizeId,
            daily_limit: formData.daily_limit,
          })
          .eq('id', editingAllocation.id);

        if (error) throw error;
        toast({ title: "Success", description: "Allocation updated successfully" });
      } else {
        const { error } = await supabase
          .from('prize_allocations')
          .insert([{
            university_id: universityId,
            prize_id: prizeId,
            daily_limit: formData.daily_limit,
          }]);

        if (error) throw error;
        toast({ title: "Success", description: "Allocation added successfully" });
      }

      setFormData({ university_id: "", prize_id: "", daily_limit: 1 });
      onSuccess();
    } catch (error: any) {
      console.error('Error saving allocation:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save allocation",
      });
    }
  };

  return {
    formData,
    setFormData,
    handleSubmit
  };
}
